import {
  patchV1CompaniesCompanyIdProjectsProjectIdRateReference,
  type RateReference_Request,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import connection from '~services/connectionModule';

// Consumes horizon-api PR 8485. The parsed result of a successful `rate_reference`
// response, mapped to the FE's camelCase convention (the BE response carries snake_case fields).
// TRANSITIONS ONLY — the ids the BE ACTUALLY changed this call (no-ops omitted):
//   - `referencedOrderIds`   — orders newly re-referenced (re-synced) this call.
//   - `dereferencedOrderIds` — orders newly dereferenced (decoupled) this call.
// Both empty ⇒ the reconcile was a pure no-op (nothing changed). Callers use that to decide whether
// a durable transition actually landed (e.g. `rateReferenceApplied` messaging).
interface RateReferenceResult {
  referencedOrderIds: string[];
  dereferencedOrderIds: string[];
}

interface ApplyProjectRateReferenceArgs {
  companyId: string;
  projectId: string;
  // The COMPLETE set of orders that should end up REFERENCED (synced to the project card). Every
  // reachable order NOT in this list is dereferenced by the BE; a listed order that is currently
  // dereferenced is re-referenced. This is the single invariant the endpoint rests on — the caller
  // MUST pass the complete in-range referenced set (loaded to exhaustion), never a partial page.
  referencedOrderIds: string[];
  // Required (true) ONLY when the request would leave the project with ZERO referenced orders (a
  // deliberate Deselect-All / all-past forward-looking default). The BE returns 422 without it. Set
  // by the caller only after a user confirmation. Omitted otherwise.
  confirmFullDereference?: boolean;
  // Order-origin edit only: the editing order's own id, forced into the referenced set by the BE so
  // it is never dereferenced (the origin always keeps its own edit). Omitted for project-origin saves.
  originOrderId?: string;
}

/**
 * Consumes horizon-api PR 8485. Reconciles a project's REFERENCED order set in ONE
 * idempotent, order-level call — independent of any rate-card write. FreightRates issues this ONCE
 * per save, BEFORE the SRS / add-on / material writes, so deselected (and out-of-range) orders are
 * decoupled before any cascade runs and the subsequent cascade never touches them.
 *
 * Single-list set-math (supersedes the old delta contract): the client sends the COMPLETE set of
 * orders that should be referenced; the BE dereferences everything reachable that is NOT in the list
 * (past / unchecked included) and re-references any listed order that was previously dereferenced.
 *
 * Idempotent: a full retry after a partial-save failure is safe.
 */
export async function applyProjectRateReference({
  companyId,
  projectId,
  referencedOrderIds,
  confirmFullDereference,
  originOrderId,
}: ApplyProjectRateReferenceArgs): Promise<RateReferenceResult> {
  // STRICT BODY — `RateReference_Request` is `additionalProperties:false`: the endpoint REJECTS (4xx)
  // any key other than these. Typing the literal as `RateReference_Request` makes the strict-key
  // invariant compiler-enforced — a leaked `skipped_order_ids` / `from` / `to` (e.g. from spreading
  // an upstream object) is a type error, not a runtime 4xx. Never spread an upstream object here.
  const body: RateReference_Request = {
    referenced_order_ids: referencedOrderIds,
    ...(confirmFullDereference ? { confirm_full_dereference: true } : {}),
    ...(originOrderId ? { origin_order_id: originOrderId } : {}),
  };

  // The `rate_reference` route (horizon-api PR 8485) is in the published api-spec
  // (@treadinc/horizon-api-spec 4.1.1785450303), so we call the generated SDK function directly (no
  // raw-client cast). `throwOnError: true` makes a non-2xx reject — the caller (FreightRates) aborts
  // the save and can safely retry (idempotent).
  try {
    const response = await patchV1CompaniesCompanyIdProjectsProjectIdRateReference({
      path: { 'company-id': companyId, 'project-id': projectId },
      body,
      throwOnError: true,
    });

    const data = response.data?.data;
    return {
      referencedOrderIds: data?.referenced_order_ids ?? [],
      dereferencedOrderIds: data?.dereferenced_order_ids ?? [],
    };
  } catch (e) {
    // Observability (mirrors the sibling fetchers in useSiteRateSchedule): this is a load-bearing,
    // fail-closed endpoint, so a failure MUST reach Sentry, not just re-throw raw.
    // `handleRequestError` toasts + reports to Sentry and ALWAYS throws, so the inner try/catch
    // swallows that throw and we re-throw the ORIGINAL error — the caller (FreightRates) aborts the
    // save. The endpoint is idempotent, so a full retry after abort is safe.
    try {
      connection.handleRequestError(e, t('project.form.rate_reference_failed'));
    } catch {
      // Error already handled (toast + Sentry)
    }
    throw e;
  }
}
